// @avantia common-code
import { debugDisplay, SMap, voidFunction } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import { destructureControlId, HpAnswerValueTypes, HpStandardInputTextAndStyle } from '@avantia/questionset-model';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logError, logInfo } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { getTypeOfQuestion } from '../reducers/questionSetTools';
import {
  HpClientAnswerExpressionFunction,
  HpClientConditionFunction,
  HpClientConditionFunctionProps,
  HpClientDependentAction,
  HpClientDependentAnswer,
  HpClientDynamicFunctionProps,
  HpClientLookupConditionFunctionProps,
  HpClientQuestion,
  HpClientQuestionLookupItemMap,
  HpClientQuestionSetState,
  HpClientSection,
  HpEventTypes
} from '../types';
import { exceptionToString } from './clientAndServerUtilities';
import {
  HpClientPrerequisiteThisReferenceClass,
  HpClientPrerequisiteThisReferenceClassProps
} from './prerequisiteThisReference';

type HpEntityNameTypes = 'Question' | 'Section';

type DebugOutputStatus = 'been met' | 'not been met' | 'failed';

type DebugOutputFunc = (
  questionId: string,
  condition: HpClientConditionFunction,
  index: number,
  status: DebugOutputStatus,
  error?: any
) => void;

interface DebugOutputCreatorArgs {
  debugDetailFlag: HpClientDebugFlags;
  debugFlag: HpClientDebugFlags;
  exprName: string;
  getDesc: (id: string) => string;
  typeName: HpEntityNameTypes;
}

const indexToken = '{{index}}';
let clientDebugFlags: HpClientDebugFlags;
registerConfigurationAction((config) => {
  ({ clientDebugFlags } = config);
});

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function questionPrerequisitesHaveBeenMet(
  questionId: string,
  eventType: HpEventTypes,
  state: HpClientQuestionSetState
): boolean {
  const { questions } = state;
  const question = questions[questionId];
  const { data } = question;
  const { controlType } = ((data ? data.textAndStyle.standard : null) || {}) as HpStandardInputTextAndStyle;
  const isHidden = controlType === 'Hidden';
  if (isHidden) {
    return false;
  }

  return questionOrSectionPrerequisitesHaveBeenMet({
    questionOrSectionId: questionId,
    type: 'question',
    typeName: 'Question',
    eventType,
    getItem: (id) => questions[id],
    getDesc: (id) => questions[id].name,
    state
  });
}

export function sectionPrerequisitesHaveBeenMet(
  sectionId: string,
  eventType: HpEventTypes,
  state: HpClientQuestionSetState
): boolean {
  const sections = state.displayState.sections;
  return questionOrSectionPrerequisitesHaveBeenMet({
    questionOrSectionId: sectionId,
    type: 'section',
    typeName: 'Section',
    eventType,
    getItem: (id) => sections[id],
    getDesc: (id) => sections[id].title,
    state
  });
}

interface QuestionOrSectionPrerequisitesHaveBeenMetProps {
  eventType: HpEventTypes;
  getDesc: (id: string) => string;
  getItem: (id: string) => HpClientQuestion | HpClientSection;
  questionOrSectionId: string;
  state: HpClientQuestionSetState;
  type: 'question' | 'section';
  typeName: 'Question' | 'Section';
}

function questionOrSectionPrerequisitesHaveBeenMet({
  eventType,
  getDesc,
  getItem,
  questionOrSectionId,
  state,
  type,
  typeName
}: QuestionOrSectionPrerequisitesHaveBeenMetProps): boolean {
  const withPrerequisites: HpClientQuestion | HpClientSection = getItem(questionOrSectionId);
  if (!withPrerequisites) {
    throw new Error(`There is no ${type} with id "${questionOrSectionId}".`);
  }

  let { prerequisites = [] } = withPrerequisites;
  const { groupNumber, indexes, questionId } = destructureControlId(questionOrSectionId, { requirePrefix: false });
  const { answers, commonLookups, displayState, productId, questions } = state;
  const { sections } = displayState;
  const isQuestion = type === 'question';

  if (isQuestion) {
    const question = questions[questionId];
    const { groupId } = question;

    if (groupId) {
      const section = sections[groupId];
      const { isRepeater, prerequisites: sectionPrerequisites = [] } = section;
      if (isRepeater) {
        if (groupNumber === undefined) {
          return true;
        }
      }

      if (sectionPrerequisites.length > 0) {
        prerequisites = [...sectionPrerequisites, ...prerequisites];
      }
    }
  }

  if (!prerequisites.length) {
    return true;
  }

  const debugOutput = createDebugOutput({
    typeName,
    exprName: `prerequisite (${indexToken}) has`,
    debugFlag: HpClientDebugFlags.PrerequisiteDetail,
    debugDetailFlag: HpClientDebugFlags.PrerequisiteDetail,
    getDesc
  });

  const sourceQuestionId = isQuestion ? questionOrSectionId : '';
  const questionType = getTypeOfQuestion(questionOrSectionId, questions, sections);
  const self = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.PrerequisiteDetail,
    displayState,
    groupNumber,
    indexes,
    productId,
    questions,
    sections,
    sourceQuestionId,
    sourceQuestionType: questionType
  });

  const props: HpClientDynamicFunctionProps = {
    questionId: questionOrSectionId,
    questionType,
    eventType,
    sourceQuestionId,
    indexes,
    groupNumber
  };

  let isMet = true;
  let index = 0;

  for (const prerequisite of prerequisites) {
    try {
      if (!prerequisite.call(self, props)) {
        debugOutput(questionOrSectionId, prerequisite, index, 'not been met');
        isMet = false;
        break;
      } else {
        debugOutput(questionOrSectionId, prerequisite, index, 'been met');
      }
    } catch (ex) {
      logError(`Error invoking prerequisite ${index} of '${questionOrSectionId}': ${exceptionToString(ex)}`);
    }

    index++;
  }

  logInfo(
    `${typeName} "${getDesc(questionOrSectionId)}" (${questionOrSectionId}${
      groupNumber === undefined ? '' : `, group ${groupNumber}`
    }) prerequisites have${isMet ? '' : ' not'} been met.`,
    HpClientDebugFlags.Prerequisites
  );

  return isMet;
}

function createDebugOutput({
  debugDetailFlag,
  debugFlag,
  exprName,
  getDesc,
  typeName
}: DebugOutputCreatorArgs): DebugOutputFunc {
  if (!(clientDebugFlags & debugFlag)) {
    return voidFunction;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return (
    questionId: string,
    condition: HpClientConditionFunction,
    index: number,
    status: DebugOutputStatus,
    error: any
  ) => {
    const isError = status === 'failed';
    const description = getDesc(questionId);
    const message = `${typeName} "${description}" (${questionId}) ${exprName.replace(indexToken, String(index))} ${
      isError ? 'failed' : status
    }.`;
    if (!isError) {
      logInfo(message, debugFlag);
    } else {
      logError({ message, error });
    }

    if (isError || clientDebugFlags & debugDetailFlag) {
      logInfo(`${condition.toString()}`, debugFlag);
    }
  };
}

export function applyAnswerDefaults(
  props: HpPerformActionOnConditionalModelsProps<HpClientDependentAnswer>
): HpClientQuestionSetState {
  return performActionOnConditionalModels(props);
}

export function runDependentActions(
  props: HpPerformActionOnConditionalModelsProps<HpClientDependentAction>
): HpClientQuestionSetState {
  return performActionOnConditionalModels(props);
}

export function evaluateLookupConditions(
  questionId: string,
  eventType: HpEventTypes,
  lookup: HpClientQuestionLookupItemMap,
  state: HpClientQuestionSetState
): { visible: SMap<boolean>; count: number } {
  let count = 0;
  const visible: SMap<boolean> = {};
  lodash.forOwn(lookup, (item, key) => {
    if (!item) {
      return;
    } else if (!item.condition) {
      visible[key] = true;
      return;
    }

    visible[key] = false;
    count++;
    const conditionMetAction = (): undefined => {
      visible[key] = true;
      return undefined;
    };

    performActionOnConditionalModels<LookupConditionalModel>({
      items: { [questionId]: [{ ...item, lookupKey: key }] },
      state,
      conditionMetAction,
      sourceQuestionId: questionId,
      type: 'lookup condition',
      eventType,
      sectionGroupNumber: undefined
    });
  });

  return { visible, count };
}

interface LookupConditionalModel extends ConditionalModel {
  lookupKey: string;
}
interface ConditionalModel {
  condition?: HpClientConditionFunction;
}

export interface HpConditionMetFunctionProps<ItemT extends ConditionalModel> {
  questionId: string;
  item: ItemT;
  state: HpClientQuestionSetState;
}

export type HpConditionMetFunction<ItemT extends ConditionalModel> = (
  props: HpConditionMetFunctionProps<ItemT>
) => HpClientQuestionSetState | undefined;

export interface HpPerformActionOnConditionalModelsProps<ItemT extends ConditionalModel> {
  sourceQuestionId: string;
  items: SMap<ItemT[]>;
  state: HpClientQuestionSetState;
  conditionMetAction: HpConditionMetFunction<ItemT>;
  type: 'dependent answer' | 'dependent action' | 'lookup condition' | 'help info' | 'question text condition';
  eventType: HpEventTypes;
  sectionGroupNumber: number | undefined;
}

export function performActionOnConditionalModels<ItemT extends ConditionalModel>({
  sourceQuestionId,
  items: conditionalModels,
  state,
  conditionMetAction,
  eventType,
  type,
  sectionGroupNumber
}: HpPerformActionOnConditionalModelsProps<ItemT>): HpClientQuestionSetState {
  let newState = state;
  let unmetQuestionId = '';
  let unmetIndex = -1;
  let unmetCondition: HpClientConditionFunction | undefined = undefined;

  const debugOutput = createDebugOutput({
    typeName: 'Question',
    exprName: `${type} (${indexToken}) has`,
    debugFlag: HpClientDebugFlags.DependentAnswers,
    debugDetailFlag: HpClientDebugFlags.DependentAnswerDetail,
    getDesc: (id) => {
      const q = state.questions[id];
      if (!q) {
        throw new Error(`There is no question with ID "${id}".`);
      }

      return q.name || q.questionId;
    }
  });

  lodash.forOwn(conditionalModels, (defaultExprList, questionId) => {
    if (type === 'help info') {
      questionId = sourceQuestionId;
    }

    const { groupNumber: grpNum, indexes } = destructureControlId(questionId, { requirePrefix: false });
    let groupNumber = grpNum;
    if (groupNumber === undefined) {
      groupNumber = sectionGroupNumber;
    }

    let index = 0;
    for (const defaultExpr of defaultExprList) {
      let conditionMet = true;
      const { condition } = defaultExpr;

      if (condition !== undefined) {
        const { answers, commonLookups, displayState, productId, questions } = newState;
        const { sections } = displayState;
        const questionType = getTypeOfQuestion(questionId, questions, sections);
        let condProps: HpClientConditionFunctionProps | HpClientDynamicFunctionProps | undefined;

        if (type === 'lookup condition') {
          const { lookupKey } = defaultExpr as unknown as LookupConditionalModel;
          if (lookupKey) {
            const props: HpClientLookupConditionFunctionProps = {
              groupNumber,
              indexes,
              lookupKey,
              questionId,
              questionType
            };
            condProps = props;
          }
        } else {
          const props: HpClientDynamicFunctionProps = {
            eventType,
            groupNumber,
            indexes,
            questionId,
            questionType,
            sourceQuestionId
          };
          condProps = props;
        }

        if (condProps) {
          const sourceQuestionType =
            sourceQuestionId === questionId ? questionType : getTypeOfQuestion(sourceQuestionId, questions, sections);

          const selfProps: HpClientPrerequisiteThisReferenceClassProps<HpClientQuestion, HpClientSection> = {
            answers,
            commonLookups,
            debugFlag: HpClientDebugFlags.DependentAnswerDetail,
            displayState,
            groupNumber,
            indexes,
            productId,
            questions,
            sections,
            sourceQuestionId,
            sourceQuestionType
          };

          const self = new HpClientPrerequisiteThisReferenceClass(selfProps);

          try {
            conditionMet = condition.call(self, condProps);
          } catch (ex) {
            logError(`Error invoking ${type} ${index} of '${questionId}': ${exceptionToString(ex)}`);
          }
        }
      }

      if (conditionMet) {
        if (condition) {
          debugOutput(questionId, condition, index, 'been met');
        }

        newState = conditionMetAction({ questionId, item: defaultExpr, state: newState }) || newState;
        break;
      } else {
        unmetQuestionId = questionId;
        unmetCondition = condition;
        unmetIndex = index;
      }

      index++;
    }
  });

  if (unmetCondition) {
    debugOutput(unmetQuestionId, unmetCondition, unmetIndex, 'not been met');
  }

  return newState;
}

export function evaluateCalculatedAnswer(
  question: HpClientQuestion,
  state: HpClientQuestionSetState,
  eventType: HpEventTypes,
  calculatedAnswer?: HpClientAnswerExpressionFunction | undefined
): HpAnswerValueTypes {
  const { name: questionName, questionId } = question;
  let answer: HpAnswerValueTypes;
  calculatedAnswer = calculatedAnswer || question.calculatedAnswer;
  if (!calculatedAnswer) {
    throw new Error(
      `Error evalulating calculated answer for Question "${questionName}": The calculatedAnswer property is not defined.`
    );
  }

  const { groupNumber, indexes } = destructureControlId(questionId, { requirePrefix: false });
  const { answers, commonLookups, displayState, productId, questions } = state;
  const { sections } = displayState;
  const questionType = getTypeOfQuestion(questionId, questions, sections);

  const self = new HpClientPrerequisiteThisReferenceClass({
    answers,
    commonLookups,
    debugFlag: HpClientDebugFlags.CalculatedAnswerDetail,
    displayState,
    groupNumber,
    indexes,
    productId,
    questions,
    sections,
    sourceQuestionId: questionId,
    sourceQuestionType: questionType
  });

  const props: HpClientDynamicFunctionProps = {
    questionId,
    questionType,
    sourceQuestionId: questionId,
    eventType,
    indexes,
    groupNumber
  };

  try {
    answer = calculatedAnswer.call(self, props);
    logInfo(
      () => `Question "${questionName}"'s calculated answer is: ${debugDisplay(answer)}.`,
      HpClientDebugFlags.CalculatedAnswers
    );
  } catch (ex) {
    throw new Error(`Error evalulating calculated answer for Question "${questionName}": ${exceptionToString(ex)}`);
  }

  return answer;
}
